import React, { useEffect, useRef } from 'react';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import i18n from 'i18n-js';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import { BaseModal } from 'components/UI';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import * as Yup from 'yup';
import { withFormik } from 'formik';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import VersionFileInput from './VersionFileInput';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { KeyboardDatePicker } from '@material-ui/pickers';
import 'react-image-lightbox/style.css';
import useDeepCompareEffect from 'use-deep-compare-effect';

const useStyles = () => ({
  input: {
    height: 70,
    margin: 0,
  },
  select: {
    minWidth: '100%',
  },
  filesLabel: {
    color: '#4D4F5C',
    marginBottom: 2,
  },
  label: {
    color: '#2C2C2E',
  },
  leftInputContainer: {
    pr: { sm: 2 },
  },
  rightInputContainer: {
    pl: { sm: 2 },
  },
});

const VersionSchema = Yup.object().shape({
  version: Yup.string().required(),
  status: Yup.string(),
  expiration_time: Yup.date().nullable(),
  description: Yup.string(),
  default: Yup.boolean(),
  file: Yup.mixed(),
});

const VersionModal = ({
  open,
  handleClose,
  touched,
  errors,
  values,
  handleChange,
  handleBlur,
  submitForm,
  setFieldValue,
  mode,
  resetForm,
  asyncErrors = {},
  setErrors,
  setFieldTouched,
}) => {
  const defaultRef = useRef(null);
  const classes = useStyles();
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  useEffect(() => {
    if (values && defaultRef.current === null) {
      defaultRef.current = values.default;
      setFieldValue('allowSetStatus', values.default);
    }
    // eslint-disable-next-line
  }, []);

  useDeepCompareEffect(() => {
    if (asyncErrors) {
      setErrors(asyncErrors);
      Object.keys(asyncErrors).map((field) => setFieldTouched(field, true, false));
    }
  }, [asyncErrors, setErrors, setFieldTouched]);

  return (
    <BaseModal
      size="md"
      open={open}
      hasFooter={false}
      enableCloseButton={false}
      aria-labelledby="modal-title"
      title={
        mode === 'create'
          ? i18n.t('documents.create_new_version')
          : i18n.t('documents.edit_version')
      }
    >
      <form autoComplete="off">
        <Grid container size={12} direction="column" alignItems="flex-start">
          <Grid item size={12}>
            <TextField
              sx={classes.input}
              variant="standard"
              required
              fullWidth
              name="version"
              error={Boolean(touched.version && errors.version)}
              label={i18n.t('documents.version_name')}
              helperText={touched.version && errors.version ? errors.version : undefined}
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.version || ''}
            />
          </Grid>
          <Grid item size={12} direction="row" display="flex">
            {(mode === 'create' || values.allowSetStatus) && (
              <Grid item size={{ xs: 12, sm: 6 }} sx={classes.leftInputContainer}>
                <FormControl sx={{ ...classes.input, ...classes.select }}>
                  <InputLabel id="status-select-label">{i18n.t('common.status')}</InputLabel>
                  <Select
                    fullWidth
                    variant="standard"
                    labelId="status-select-label"
                    value={values.status || ''}
                    name="status"
                    onChange={handleChange}
                  >
                    <MenuItem value={'APPROVED'}>{i18n.t('documents.approved')}</MenuItem>
                    <MenuItem value={'DECLINED'}>{i18n.t('documents.declined')}</MenuItem>
                    <MenuItem value={'PENDING'}>{i18n.t('documents.pending')}</MenuItem>
                    <MenuItem value={'UPLOADED'}>{i18n.t('documents.uploaded')}</MenuItem>
                    <MenuItem value={'NONE'}></MenuItem>
                  </Select>
                  {errors.status && touched.status ? (
                    <FormHelperText error>{errors.status}</FormHelperText>
                  ) : null}
                </FormControl>
              </Grid>
            )}
            <Grid
              item
              size={{ xs: 12, sm: 6 }}
              sx={
                mode === 'create' || values?.allowSetStatus
                  ? classes.rightInputContainer
                  : classes.leftInputContainer
              }
            >
              <KeyboardDatePicker
                sx={classes.input}
                id="date-picker-dialog"
                label={i18n.t('documents.expiration_date')}
                format="MM/DD/yyyy"
                value={values.expiration_time}
                onChange={(value) => setFieldValue('expiration_time', value)}
                renderInput={(params) => (
                  <TextField
                    sx={classes.input}
                    variant="standard"
                    fullWidth
                    error={Boolean(touched.expiration_time && errors.expiration_time)}
                    helperText={
                      touched.expiration_time && errors.expiration_time
                        ? errors.expiration_time
                        : undefined
                    }
                    {...params}
                  />
                )}
              />
              {errors.expiration_time && touched.expiration_time ? (
                <FormHelperText error>{errors.expiration_time}</FormHelperText>
              ) : null}
            </Grid>
          </Grid>
          <Grid item size={12}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              inputProps={{ maxLength: 512 }}
              name="description"
              variant="standard"
              error={Boolean(touched.description && errors.description)}
              label={i18n.t('common.description')}
              helperText={
                touched.description && errors.description ? errors.description : undefined
              }
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.description || ''}
            />
          </Grid>
          <Grid item size={12}>
            <Box mt={3}>
              <FormControlLabel
                name="default"
                control={
                  <Checkbox
                    color="primary"
                    checked={values.default || false}
                    onChange={handleChange}
                  />
                }
                label={
                  <Typography sx={classes.label} variant="body2">
                    {i18n.t('documents.set_as_default_version')}
                  </Typography>
                }
                labelPlacement="end"
              />
            </Box>
          </Grid>
          {mode === 'create' ? (
            <Grid item size={12}>
              <Box mt={2}>
                <Typography sx={classes.filesLabel} variant="subtitle2" gutterBottom>
                  {values.file
                    ? i18n.t('documents.version_attachment')
                    : `${i18n.t('documents.upload_file')} (${i18n.t('common.optional')})`}
                </Typography>
              </Box>
              <VersionFileInput file={values.file} onChange={setFieldValue} name="file" />
            </Grid>
          ) : null}
          <Box mt={2.5} width="100%" role="group" display="flex" justifyContent="flex-end">
            <Box display="inline" ml={1}>
              <Button
                color="primary"
                onClick={handleClose}
                sx={{ letterSpacing: 0.7, fontWeight: 500 }}
              >
                {i18n.t('documents.cancel')}
              </Button>
            </Box>
            <Box display="inline" ml={1}>
              <Button
                color="primary"
                onClick={submitForm}
                sx={{ letterSpacing: 0.7, fontWeight: 500 }}
              >
                {mode === 'create' ? i18n.t('documents.create') : i18n.t('documents.update')}
              </Button>
            </Box>
          </Box>
        </Grid>
      </form>
    </BaseModal>
  );
};

VersionModal.propTypes = {
  /**
   * Current state modal state
   */
  open: PropTypes.bool,
  /**
   * Define the action to by applied
   */
  mode: PropTypes.oneOf(['create', 'update']),
  /**
   * Close click handler
   */
  handleClose: PropTypes.func,
  /**
   * Create click handler
   */
  handleCreate: PropTypes.func,
  /**
   * Version's name
   */
  version: PropTypes.string,
  /**
   * Version's status
   */
  status: PropTypes.string,
  /**
   * Version's expiration date
   */
  expiration_time: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  /**
   * Versions' description
   */
  description: PropTypes.string,
  /**
   * Version's file
   */
  file: PropTypes.any,
  /**
   * Version should be marked as default
   */
  default: PropTypes.any,
  // Formik props
  touched: PropTypes.object,
  errors: PropTypes.object,
  values: PropTypes.object,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  submitForm: PropTypes.func,
  setFieldValue: PropTypes.func,
  resetForm: PropTypes.func,
  asyncErrors: PropTypes.object,
  setErrors: PropTypes.func,
  setFieldTouched: PropTypes.func,
  allowSetStatus: PropTypes.bool,
};

VersionModal.defaultProps = {
  open: false,
  handleClose: undefined,
  handleCreate: undefined,
  version: undefined,
  status: '',
  expiration_time: null,
  description: undefined,
  file: undefined,
  default: false,
  touched: {},
  errors: {},
  values: {},
  handleChange: undefined,
  handleBlur: undefined,
  submitForm: undefined,
  setFieldValue: undefined,
  resetForm: undefined,
  asyncErrors: {},
  setErrors: undefined,
  setFieldTouched: undefined,
  allowSetStatus: false,
};

export default withFormik({
  mapPropsToValues: (props) => ({
    version: props.version,
    status: props.status || '',
    expiration_time: props.expiration_time || null,
    description: props.description,
    file: props.file,
    default: props.default,
    mode: props.mode || 'create',
    allowSetStatus: props.allowSetStatus,
  }),
  displayName: 'VersionModalForm',
  validationSchema: VersionSchema,
  handleSubmit: (
    {
      file,
      mode,
      status,
      expiration_time,
      default: isDefault,
      description,
      allowSetStatus,
      ...values
    },
    { props: { handleCreate } }
  ) =>
    handleCreate({
      ...values,
      ...(typeof isDefault === 'boolean' && { default: isDefault }),
      ...(expiration_time && {
        expiration_time,
      }),
      ...(status &&
        (mode === 'create' || allowSetStatus) && {
          status,
        }),
      ...(file && {
        data: file,
      }),
      ...(description && {
        description,
      }),
    }),
})(VersionModal);
